import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { matchCounties, resolveCounty } from './address';

/* =====================================================
   Câmp „Județ" cu listă și completare la tastare (combobox ARIA).
   Lista apare la focus cu toate județele; tastarea o filtrează fără
   diacritice („iasi" → Iași). La ieșirea din câmp textul devine numele
   canonic; un text care nu e un județ blochează trimiterea formularului.
===================================================== */
const CountyCombobox = ({ id, name, value, onChange, required }) => {
  const listId = useId();
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(-1);

  const options = useMemo(() => matchCounties(query), [query]);
  const resolved = resolveCounty(value);

  useEffect(() => {
    inputRef.current?.setCustomValidity(value.trim() && !resolved ? 'Alege județul din listă.' : '');
  }, [value, resolved]);

  useEffect(() => {
    if (open && active >= 0) listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const openList = (nextQuery) => {
    const nextOptions = matchCounties(nextQuery);
    setQuery(nextQuery);
    // Cu un județ deja ales, lista pornește de la el; la tastare, de la prima potrivire.
    setActive(nextQuery ? (nextOptions.length ? 0 : -1) : nextOptions.indexOf(resolved));
    setOpen(true);
  };

  const select = (county) => {
    onChange(county);
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) openList(resolved ? '' : value);
      else setActive((index) => Math.min(index + 1, options.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (open) setActive((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      if (open && options[active]) {
        event.preventDefault(); // altfel Enter ar trimite formularul de plată
        select(options[active]);
      }
    } else if (event.key === 'Escape') {
      if (open) {
        event.preventDefault();
        setOpen(false);
      }
    } else if (event.key === 'Tab') {
      if (open && query && options[active]) select(options[active]);
    }
  };

  return (
    <div className="pm-combo">
      <input
        ref={inputRef}
        id={id}
        name={name}
        className="pm-input"
        value={value}
        required={required}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={listId}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        onFocus={() => openList(resolved ? '' : value)}
        onClick={() => !open && openList(resolved ? '' : value)}
        onChange={(event) => {
          onChange(event.target.value);
          openList(event.target.value);
        }}
        onKeyDown={onKeyDown}
        onBlur={() => {
          setOpen(false);
          if (resolved && resolved !== value) onChange(resolved);
        }}
      />
      {open && (
        <ul ref={listRef} id={listId} role="listbox" aria-label="Județe" className="pm-combo-list">
          {options.length === 0 && (
            <li className="pm-combo-empty" role="presentation">
              Niciun județ nu se potrivește.
            </li>
          )}
          {options.map((county, index) => (
            <li
              key={county}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === active}
              className={`pm-combo-option${index === active ? ' is-active' : ''}${county === resolved ? ' is-selected' : ''}`}
              // mousedown, nu click: altfel blur-ul închide lista înainte de alegere
              onMouseDown={(event) => {
                event.preventDefault();
                select(county);
              }}
              onMouseEnter={() => setActive(index)}
            >
              {county}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CountyCombobox;
